/* eslint-disable no-unused-vars */
import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Field, Form } from 'react-final-form';
import formatStringByPattern from 'format-string-by-pattern';
import { CameraIcon } from '@heroicons/react/solid';
import Header from '../components/Header';
import {
  composeValidators,
  email,
  renderField,
  required,
  usPhoneNumber
} from '../helpers/form-validations';
import withStateDispatch from '../redux/withStateDispatch';
import { checkPermission } from '../helpers/app';
import { getProfile, updateProfile, uploadFile } from '../utils/firebase';
import UpdateProfileModal from '../components/UpdateProfileModal';
import ProfileCropper from '../components/ProfileCropper';

const Profile = ({ user, setLoader, setNotify, setUser }) => {
  const navigate = useNavigate();
  const [initialValues, setInitialValues] = useState({});
  const [isUploadModalOpen, setIsUploadModalOpen] = useState(false);

  const [previewImage, setPreviewImage] = useState(null); // objectURL preview
  const [showCropper, setShowCropper] = useState(false);

  const getLoginUserProfile = async () => {
    setLoader(true);
    const profileRes = await getProfile();

    if (profileRes) {
      setInitialValues({
        avatar: profileRes?.avatar || '',
        fname: profileRes?.name?.first || '',
        lname: profileRes?.name?.last || '',
        email: profileRes?.email?.primary || '',
        phone: profileRes?.phone?.mobile || '',
        title: profileRes?.title || '',
        bio: profileRes?.bio || '',
        line1: profileRes?.highlights?.[0] || '',
        line2: profileRes?.highlights?.[1] || '',
        line3: profileRes?.highlights?.[2] || '',
        line4: profileRes?.highlights?.[3] || ''
      });
      setUser({
        ...user,
        name: {
          first: profileRes.name.first,
          last: profileRes.name.last
        },
        avatar: profileRes.avatar,
        email: profileRes.email.primary,
        mobile: profileRes.phone.mobile,
        title: profileRes.title
      });
    }
    setLoader(false);
  };

  const onSubmit = async (formData) => {
    const payload = {
      name: {
        first: formData.fname || '',
        last: formData.lname || ''
      },
      email: {
        primary: formData.email || ''
      },
      phone: {
        mobile: formData.phone || ''
      },
      title: formData.title || '',
      bio: formData.bio || '',
      highlights: [
        formData.line1 || '',
        formData.line2 || '',
        formData.line3 || '',
        formData.line4 || ''
      ].filter((line) => line.trim() !== '') // remove empty lines
    };

    try {
      setLoader(true);
      const response = await updateProfile(payload);
      if (response) {
        getLoginUserProfile();
        setNotify({ message: 'Profile updated successfully.', type: 'success' });
      } else {
        setNotify({ message: 'Profile update failed! Please try again.', type: 'error' });
      }
    } catch (error) {
      setNotify({ message: 'Profile update failed! Please try again.', type: 'error' });
    } finally {
      setLoader(false);
    }
  };

  const uploadImage = async (file) => {
    try {
      setLoader(true);
      // Ensure file has a name for firebase utils, defaulting to png if missing (common with blobs)
      const fileToUpload = file.name
        ? file
        : new File([file], 'avatar.png', { type: file.type || 'image/png' });

      const avatar = await uploadFile('profile', fileToUpload);

      if (!avatar) {
        throw new Error('Failed to get avatar URL');
      }

      const payload = {
        avatar
      };
      const response = await updateProfile(payload);
      if (response) {
        await getLoginUserProfile();
        setNotify({ type: 'success', message: 'Avatar uploaded successfully.' });
      } else {
        setNotify({ type: 'error', message: 'Avatar upload failed! Please try again.' });
      }
    } catch (error) {
      console.error('Avatar upload error:', error);
      setNotify({ type: 'error', message: 'Avatar upload failed! Please try again.' });
    } finally {
      setLoader(false);
    }
  };

  const handleFileSelect = (e) => {
    const file = e.target.files[0];
    if (file) {
      // Update the avatar image preview
      const imgURL = URL.createObjectURL(file);
      document.getElementById('avatar').src = imgURL;
      setPreviewImage(imgURL);
      setShowCropper(true); // open cropper
    }
  };

  const onMakeAvatar = async (croppedBlob) => {
    setShowCropper(false);
    const imgURL = URL.createObjectURL(croppedBlob);
    document.getElementById('avatar').src = imgURL;
    setPreviewImage(imgURL);
    if (croppedBlob) {
      await uploadImage(croppedBlob);
    }
  };

  const openCamera = () => {
    const input = document.createElement('input');
    input.type = 'file';
    input.accept = 'image/*';
    input.capture = 'environment'; // Opens camera (rear)
    input.style.display = 'none'; // Hidden input
    input.onchange = handleFileSelect;
    document.body.appendChild(input); // Required for some browsers
    input.click();
    document.body.removeChild(input); // Clean up
  };

  const openGallery = () => {
    const input = document.createElement('input');
    input.type = 'file';
    input.accept = 'image/*';
    input.style.display = 'none'; // Hidden input
    input.onchange = handleFileSelect;
    document.body.appendChild(input);
    input.click();
    document.body.removeChild(input);
  };

  useEffect(() => {
    checkPermission(user, navigate);
  }, []);

  useEffect(() => {
    getLoginUserProfile();
  }, []);

  return (
    <div className="max-w-screen-md mx-auto">
      <Header
        title="Profile"
        subtitle="Update your profile"
        showBack
        showProfile={false}
        onBackClick={() => {
          navigate('/inventory', { replace: true });
        }}
      />

      <div className="flex flex-col gap-[30px] px-5 pt-6">
        <Form
          onSubmit={onSubmit}
          initialValues={initialValues}
          render={({ handleSubmit, errors, touched }) => (
            <form onSubmit={handleSubmit}>
              <div className="flex flex-col gap-y-5 pb-32">
                <div
                  className="relative cursor-pointer mx-auto"
                  onClick={() => setIsUploadModalOpen(true)}>
                  <img
                    id="avatar"
                    name="avatar"
                    className="w-28 h-28 bg-gray-300 rounded-full flex-shrink-0"
                    src={initialValues.avatar}
                    alt="user_avatar"
                  />
                  <div className="opacity-75 duration-300 absolute inset-0 top-[65%] flex items-center justify-center text-xl bg-white text-red-700 font-semibold">
                    <div className="p-1 rounded-full border-2 bg-red-600 border-red-600">
                      <CameraIcon className="h-5 w-5 text-white" />
                    </div>
                  </div>
                </div>
                <div className="flex flex-col gap-2">
                  <label
                    className={`text-base font-medium ${
                      touched.fname && errors.fname && 'text-red-500'
                    }`}>
                    First Name *
                  </label>
                  <Field
                    type="text"
                    name="fname"
                    component="input"
                    className={`rounded-md border px-4 py-4 focus-visible:outline-none ${
                      touched.fname && errors.fname
                        ? 'border-red-500'
                        : 'border-[#555555] border-opacity-40'
                    }`}
                    placeholder="eg: James"
                    validate={composeValidators(required)}>
                    {renderField}
                  </Field>
                </div>
                <div className="flex flex-col gap-2">
                  <label
                    className={`text-base font-medium ${
                      touched.lname && errors.lname && 'text-red-500'
                    }`}>
                    Last Name *
                  </label>
                  <Field
                    type="text"
                    name="lname"
                    component="input"
                    className={`rounded-md border px-4 py-4 focus-visible:outline-none ${
                      touched.lname && errors.lname
                        ? 'border-red-500'
                        : 'border-[#555555] border-opacity-40'
                    }`}
                    placeholder="eg: Petter"
                    validate={composeValidators(required)}>
                    {renderField}
                  </Field>
                </div>

                <div className="flex flex-col gap-2">
                  <label
                    className={`text-base font-medium ${
                      touched.email && errors.email && 'text-red-500'
                    }`}>
                    Email *
                  </label>
                  <Field
                    type="text"
                    name="email"
                    component="input"
                    className={`rounded-md border px-4 py-4 focus-visible:outline-none ${
                      touched.email && errors.email
                        ? 'border-red-500'
                        : 'border-[#555555] border-opacity-40'
                    }`}
                    placeholder="eg: jamespeterleyton54@gmail.com"
                    validate={composeValidators(email, required)}>
                    {renderField}
                  </Field>
                </div>

                <div className="flex flex-col gap-2">
                  <label
                    className={`text-base font-medium ${
                      touched.phone && errors.phone && 'text-red-500'
                    }`}>
                    Phone *
                  </label>
                  <Field
                    type="text"
                    name="phone"
                    component="input"
                    className={`rounded-md border px-4 py-4 focus-visible:outline-none ${
                      touched.phone && errors.phone
                        ? 'border-red-500'
                        : 'border-[#555555] border-opacity-40'
                    }`}
                    placeholder="eg: (402) 555-1212"
                    parse={formatStringByPattern('(999) 999-9999')}
                    validate={composeValidators(usPhoneNumber, required)}>
                    {renderField}
                  </Field>
                </div>

                <div className="flex flex-col gap-2">
                  <label
                    className={`text-base font-medium ${
                      touched.title && errors.title && 'text-red-500'
                    }`}>
                    Title *
                  </label>
                  <Field
                    type="text"
                    name="title"
                    component="input"
                    className={`rounded-md border px-4 py-4 focus-visible:outline-none ${
                      touched.title && errors.title
                        ? 'border-red-500'
                        : 'border-[#555555] border-opacity-40'
                    }`}
                    placeholder="eg: Sales Agent"
                    validate={composeValidators(required)}>
                    {renderField}
                  </Field>
                </div>

                <div className="flex flex-row gap-2 items-center justify-center">
                  <div className="flex-1 h-0.5 bg-gray-200" />
                  <label className="flex">Tell customers about yourself</label>
                  <div className="flex-1 h-0.5 bg-gray-200" />
                </div>
                <label className="text-xs opacity-50">
                  Use 355 characters to describe yourself however you like
                </label>
                <div className="flex flex-col gap-2">
                  <label className="text-base font-medium">Tell us a little about yourself</label>
                  <Field
                    type="textarea"
                    name="bio"
                    component="input"
                    className="rounded-md border px-4 py-4 focus-visible:outline-none border-[#555555] border-opacity-40"
                    placeholder="eg: Passionate about helping clients find their dream home...">
                    {renderField}
                  </Field>
                </div>

                <div className="flex flex-row gap-2 items-center justify-center">
                  <div className="flex-1 h-0.5 bg-gray-200" />
                  <label className="flex">Highlights</label>
                  <div className="flex-1 h-0.5 bg-gray-200" />
                </div>
                <label className="text-xs opacity-50">
                  Use 78 characters to describe something about yourself. Suggestions are just that.
                </label>
                {['line1', 'line2', 'line3', 'line4'].map((line, index) => (
                  <div className="flex flex-col gap-2" key={line}>
                    <label className="text-base font-medium">Line {index + 1}</label>
                    <Field
                      type="text"
                      name={line}
                      component="input"
                      className="rounded-md border px-4 py-4 focus-visible:outline-none border-[#555555] border-opacity-40"
                      placeholder={
                        index === 0
                          ? 'eg: 10+ years in sales'
                          : index === 1
                          ? 'eg: Expert in valuation'
                          : index === 2
                          ? 'eg: Certified negotiator'
                          : index === 3
                          ? 'eg: Award-winning agent'
                          : 'eg:'
                      }>
                      {renderField}
                    </Field>
                  </div>
                ))}

                <button
                  type="submit"
                  className="max-w-screen-md mx-auto fixed bottom-5 left-5 right-5 text-lg text-white bg-[#5046E4] rounded-md py-4">
                  Update Profile
                </button>
              </div>
            </form>
          )}
        />
      </div>
      <UpdateProfileModal
        modal={isUploadModalOpen}
        onClick={(item) => {
          // TODO if camera open camera else gallery and then call the uploadApi for image
          if (item === 'camera') {
            openCamera();
          } else if (item === 'gallery') {
            openGallery();
          }
          setIsUploadModalOpen(false);
        }}
        onOutsideClick={() => setIsUploadModalOpen(false)}
      />

      {showCropper && (
        <ProfileCropper
          image={previewImage}
          modal={showCropper}
          onClick={(item) => {
            onMakeAvatar(item);
          }}
          onOutsideClick={() => setShowCropper(false)}
        />
      )}
    </div>
  );
};

export default withStateDispatch(Profile);
